import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtAUD, getToken, formatErr } from "../lib/api";
import { ArrowLeft, Download, Printer, Mail, Loader2, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import ezyLogo from "../assets/ezy-logo.webp";

export default function QuoteDetail() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => { api.get(`/quotes/${id}`).then((r) => setQuote(r.data)); }, [id]);

  const downloadPdf = async (customerView = false) => {
    const url = `${api.defaults.baseURL}/quotes/${id}/pdf${customerView ? "?customer_view=1" : ""}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${quote?.quote_number || "quote"}${customerView ? "-customer" : ""}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  if (!quote) return <div className="text-sm text-gray-400">Loading…</div>;

  const sel = quote.selected_scope || "wholesale";
  const sc = quote.calculation?.scopes?.[sel] || {};
  const isMulti = Array.isArray(quote.items) && quote.items.length > 0;
  const headlineTotal = isMulti ? (quote.combined_total || 0) : sc.sale_price;
  const headlineLabel = isMulti ? "Combined Total" : `${sc.label || "—"} Total`;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="quote-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/quote-portal/quotes" className="text-sm text-[#254391] hover:text-[#fda500] flex items-center gap-1">
          <ArrowLeft size={14} /> Back to quotes
        </Link>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="px-3.5 py-2 text-sm border border-gray-300 hover:border-[#254391] rounded-md flex items-center gap-1.5">
            <Printer size={13} /> Print
          </button>
          <button onClick={() => setEmailOpen(true)} className="px-3.5 py-2 text-sm border border-[#254391] text-[#254391] hover:bg-[#254391] hover:text-white rounded-md flex items-center gap-1.5" data-testid="email-quote-btn">
            <Mail size={13} /> Email
          </button>
          <button onClick={() => downloadPdf(true)} className="px-3.5 py-2 text-sm border border-[#254391] text-[#254391] hover:bg-[#254391] hover:text-white rounded-md flex items-center gap-1.5" data-testid="download-customer-pdf-btn">
            <Download size={13} /> Customer PDF
          </button>
          <button onClick={() => downloadPdf(false)} className="bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold px-4 py-2 rounded-md flex items-center gap-2 shadow-md" data-testid="download-pdf-btn">
            <Download size={14} /> Internal PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-[#0A1128] text-white p-6 md:p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#fda500]" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <img src={ezyLogo} alt="EAS" className="h-14 w-auto bg-white p-2 rounded shadow" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold">Ezy Access Solutions</div>
                <div className="text-2xl md:text-3xl font-bold tracking-tight">Quote {quote.quote_number}</div>
                <div className="text-white/60 text-sm mt-0.5">{quote.customer_name} · {(quote.created_at||"").slice(0,10)}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#fda500] font-bold">{headlineLabel}</div>
              <div className="font-mono text-3xl md:text-4xl font-bold">{fmtAUD(headlineTotal)}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b border-gray-100">
          <Field label="Customer">{quote.customer_name}</Field>
          <Field label="Email">{quote.customer_email || "—"}</Field>
          <Field label="Phone">{quote.customer_phone || "—"}</Field>
          <Field label="Address">{quote.customer_address || "—"}</Field>
        </div>

        {isMulti && (
          <div className="p-6 border-b border-gray-100" data-testid="multi-items-section">
            <SectionTitle>Quote Line Items ({quote.items.length})</SectionTitle>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                    <th className="px-3 py-2.5 font-semibold">#</th>
                    <th className="px-3 py-2.5 font-semibold">Product</th>
                    <th className="px-3 py-2.5 font-semibold">Scope</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Line Price</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((it, idx) => (
                    <tr key={it.id || `${it.product_type}-${idx}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{idx + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-[#0A1128]">{it.label || it.product_type}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{it.product_type}</div>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        <span className="inline-block bg-[#fff8e7] border border-[#fda500]/40 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded">
                          {(it.selected_scope || "").replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold text-[#0A1128]">{fmtAUD(it.line_price || 0)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td colSpan={3} className="px-3 py-3 font-bold text-right text-[#0A1128]">Combined Total</td>
                    <td className="px-3 py-3 text-right font-mono font-bold text-[#254391] text-base">{fmtAUD(quote.combined_total || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="p-6 border-b border-gray-100">
          <SectionTitle>Specification</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(quote.inputs || {}).map(([k, v]) => (
              <Spec key={k} label={k.replace(/_/g, " ")} value={typeof v === "boolean" ? (v ? "Yes" : "No") : v} />
            ))}
            {quote.calculation?.weight?.total_kg != null && (
              <Spec label="Total Weight" value={`${quote.calculation.weight.total_kg} kg`} accent />
            )}
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <SectionTitle>All Scopes</SectionTitle>
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(quote.calculation.scopes).map(([k, v]) => (
                  <tr key={k} className={`border-b border-gray-100 ${k === sel ? "bg-[#fff8e7]" : ""}`}>
                    <td className="py-2.5 text-sm font-medium">{v.label}</td>
                    <td className="py-2.5 text-xs text-gray-500 font-mono">{Math.round(v.margin_pct*100)}%</td>
                    <td className="py-2.5 text-right font-mono font-semibold text-[#0A1128]">{fmtAUD(v.sale_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <SectionTitle>Weight Breakdown</SectionTitle>
            <table className="w-full text-sm">
              <tbody>
                {quote.calculation?.weight && Object.entries(quote.calculation.weight).filter(([k]) => k !== "total_kg").map(([k, v]) => (
                  <tr key={k} className="border-b border-gray-100"><td className="py-2.5 text-gray-600">{k.replace(/_/g, " ").replace("kg", "")}</td><td className="py-2.5 text-right font-mono">{v} kg</td></tr>
                ))}
                <tr><td className="py-2.5 font-bold text-[#0A1128]">TOTAL</td><td className="py-2.5 text-right font-mono font-bold text-[#254391] text-base">{quote.calculation?.weight?.total_kg} kg</td></tr>
              </tbody>
            </table>
            {quote.notes && (
              <div className="mt-5">
                <SectionTitle>Notes</SectionTitle>
                <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-md border border-gray-200">{quote.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {emailOpen && <EmailQuoteModal quote={quote} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

function EmailQuoteModal({ quote, onClose }) {
  const [to, setTo] = useState(quote.customer_email || "");
  const [subject, setSubject] = useState(`Your Ezy Access Quote — ${quote.quote_number}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post(`/quotes/${quote.id}/email`, { to: to.trim().toLowerCase(), subject, message });
      setDone(true);
      setTimeout(onClose, 1800);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="email-quote-modal">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-[0.14em] text-[#254391] flex items-center gap-2"><Mail size={14} /> Email Quote</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">To</label>
            <input type="email" required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-[#254391] outline-none" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to-input" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">Subject</label>
            <input type="text" required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-[#254391] outline-none" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">Message (optional)</label>
            <textarea rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-[#254391] outline-none" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a personal note for the customer…" data-testid="email-message-input" />
          </div>
          <div className="text-[11px] text-gray-500 italic">PDF quote will be attached automatically.</div>
          {err && <div className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-start gap-2"><AlertTriangle size={13} className="mt-0.5" />{err}</div>}
          {done && <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md flex items-start gap-2"><CheckCircle2 size={13} className="mt-0.5" />Email sent successfully.</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy || done} className="px-4 py-2 text-sm bg-[#fda500] hover:bg-[#e59500] disabled:opacity-50 text-[#0A1128] font-bold rounded-md flex items-center gap-2 shadow-md" data-testid="email-send-btn">
              {busy ? <><Loader2 size={13} className="animate-spin" /> Sending…</> : (done ? "Sent" : "Send Email")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#254391] mb-3">{children}</div>
);
const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold mb-1">{label}</div>
    <div className="text-sm">{children}</div>
  </div>
);
const Spec = ({ label, value, accent = false }) => (
  <div className={`pl-3 border-l-2 ${accent ? "border-[#fda500] bg-[#fff8e7]/40" : "border-[#254391]"}`}>
    <div className="text-[9px] uppercase tracking-[0.18em] text-gray-500 font-bold">{label}</div>
    <div className={`font-mono text-sm ${accent ? "font-bold text-[#0A1128]" : ""}`}>{value}</div>
  </div>
);
